import { PacingStatus, PacingType } from "../../../../types";
import { getPacingStatus } from "../../../../utils";
// The looker-gateway pacing v2 query contract.
export * from "./api";
export const isPacingV2LineItem = (pacingEntry) => {
    return "campaignId" in pacingEntry;
};
export const isPacingV2Campaign = (pacingEntry) => {
    return "lineItems" in pacingEntry;
};
/** Normalizes Looker's `goal_interval` ("DAY" | "LIFETIME") to our enum. */
const normalizeGoalInterval = (goalInterval) => goalInterval?.toUpperCase() === "DAY" ? "day" : "lifetime";
/** The numeric metric fields summed when rolling line items up to a campaign. */
const SUMMED_METRIC_FIELDS = [
    "periodGoal",
    "perDayGoal",
    "periodDelivered",
    "periodDeliveredImpressions",
    "periodDeliveredSpend",
    "periodRemainingImpressions",
    "periodRemainingBudget",
    "flightGoal",
    "flightDeliveredImpressions",
    "flightDeliveredSpend",
    "flightRemainingImpressions",
    "flightRemainingBudget"
];
// Omits the pair entirely when the id is missing — a "" key would be a
// filter option a user can select but that can never actually filter
// anything (an empty `filters.selects.product` short-circuits as "no filter").
const toProductsMap = (id, name) => (id ? new Map([[id, name ?? ""]]) : new Map());
const fromLookerRow = (entry) => ({
    // IDs back routing and the campaignId grouping key below, so a null is
    // coerced to "" here rather than threaded through as a display value.
    campaignId: entry["vw_dim_lineitem.campaign__id"] ?? "",
    campaignName: entry["vw_dim_lineitem.campaign__name"],
    campaignStartDate: entry["vw_dim_lineitem.campaign_start_date"],
    campaignEndDate: entry["vw_dim_lineitem.campaign_end_date"],
    campaignStartDateRaw: entry["vw_dim_lineitem.campaign_start_raw"],
    campaignEndDateRaw: entry["vw_dim_lineitem.campaign_end_raw"],
    lineItem: {
        id: entry["vw_dim_lineitem.lineitem__id"] ?? "",
        campaignId: entry["vw_dim_lineitem.campaign__id"] ?? "",
        name: entry["vw_dim_lineitem.lineitem__name"],
        pacingType: entry["vw_dim_lineitem.lineitem_type"],
        goalInterval: normalizeGoalInterval(entry["vw_dim_lineitem.goal_interval"]),
        pacingStatus: PacingStatus.WAITING,
        timeStatus: PacingType.WAITING,
        periodGoal: entry["vw_dim_lineitem.period_goal"],
        perDayGoal: entry["vw_dim_lineitem.per_day_goal"],
        periodDelivered: entry["vw_dim_lineitem.period_delivered"],
        periodDeliveredImpressions: entry["vw_dim_lineitem.period_delivered_impressions"],
        periodDeliveredSpend: entry["vw_dim_lineitem.period_delivered_spend"],
        periodRemainingImpressions: entry["vw_dim_lineitem.period_remaining_impressions"],
        periodRemainingBudget: entry["vw_dim_lineitem.period_remaining_budget"],
        flightGoal: entry["vw_dim_lineitem.flight_goal"],
        flightDeliveredImpressions: entry["vw_fct_impressions.flight_delivered_impressions"] ?? 0,
        flightDeliveredSpend: entry["vw_fct_impressions.flight_delivered_spend"] ?? 0,
        flightRemainingImpressions: entry["vw_fct_impressions.flight_remaining_impressions"] ?? 0,
        flightRemainingBudget: entry["vw_fct_impressions.flight_remaining_budget"] ?? 0,
        stationName: entry["vw_dim_lineitem.station__name"] || "--",
        advertiserName: entry["vw_dim_lineitem.advertiser__name"],
        advertiserId: entry["vw_dim_lineitem.advertiser__id"] ?? "",
        startDate: entry["vw_dim_lineitem.lineitem_start_date"],
        endDate: entry["vw_dim_lineitem.lineitem_end_date"],
        startDateRaw: entry["vw_dim_lineitem.lineitem_start_raw"],
        endDateRaw: entry["vw_dim_lineitem.lineitem_end_raw"],
        products: toProductsMap(entry["vw_dim_lineitem.product__id"], entry["vw_dim_lineitem.product__name"]),
        advertiserCategoryName: entry["vw_dim_lineitem.advertiser__iab_category__raw__name"],
        advertiserCategoryId: entry["vw_dim_lineitem.advertiser__iab_category__raw__code"]
    }
});
// Matches Looker's raw-timeframe shape for a BigQuery TIMESTAMP: a bare
// "YYYY-MM-DD HH:MM:SS[.ffffff]" instant (T or space separator), with or
// without a literal "UTC" suffix — neither has a +HH:MM offset `new Date`
// can rely on without falling back to engine-specific legacy parsing.
const RAW_INSTANT_NO_ZONE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(?: UTC)?$/;
/**
 * Parses a Looker `raw` timeframe value as an absolute instant. A bare or
 * UTC-suffixed string is built via `Date.UTC` instead of the normal string
 * parse, so the result never depends on engine- or runtime-specific parsing.
 */
const parseRawInstant = (raw) => {
    const match = RAW_INSTANT_NO_ZONE.exec(raw ?? "");
    if (!match) {
        return new Date(raw).getTime();
    }
    const [, year, month, day, hour, minute, second, fraction] = match;
    // Truncate (not round) to 3 digits, matching how `new Date` itself
    // truncates a Z-suffixed value's fractional seconds — rounding could
    // overflow into the next second where the Z-suffixed form would not.
    const ms = fraction ? Number(fraction.padEnd(3, "0").slice(0, 3)) : 0;
    return Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second), ms);
};
/**
 * Where an entry sits in its own flight. Purely a function of dates — the shared
 * `getPacingType` also folds in a zero-delivery `ACTIVE_WAITING` state, which was
 * only ever meaningful on the legacy screen where the analysis window could be set
 * in the future. Pacing v2 always measures up to now, so an in-flight entry is
 * ACTIVE whether or not it has delivered; not delivering is a pace concern.
 */
const deriveTimeStatus = (currentTime, startTime, endTime) => {
    if (startTime > currentTime) {
        return PacingType.WAITING;
    }
    if (endTime < currentTime) {
        return PacingType.COMPLETED;
    }
    return PacingType.ACTIVE;
};
/**
 * Derives the interval-aware pace + time status for a pacing entry (and, for a
 * campaign, its line items). Pacing is measured on the period horizon:
 * `periodDelivered / periodGoal`. Mixed-interval campaigns are still computed
 * normally here — suppressing their (meaningless) metrics is a UI concern.
 *
 * An in-flight entry that has never delivered across its whole flight reports
 * `ACTIVE_WAITING` ("Not Yet Delivering"), since a pace percentage against zero
 * lifetime delivery says nothing useful. That is deliberately keyed off *flight*
 * delivery, not the period: on a DAY interval the period is today, so an entry
 * that ran yesterday and is dark today is failing to deliver, not yet to start.
 */
const derivePacing = (pacingEntry) => {
    const delivered = pacingEntry.periodDelivered;
    const pacingOverGoal = pacingEntry.periodGoal ? delivered / pacingEntry.periodGoal : 0;
    const flightDelivered = pacingEntry.pacingType === "Impressions"
        ? pacingEntry.flightDeliveredImpressions
        : pacingEntry.flightDeliveredSpend;
    // startDateRaw/endDateRaw, not startDate/endDate — the latter collapse to
    // the same midnight instant for a same-day flight, which then reads as
    // already COMPLETED the moment the clock passes midnight.
    const currentTime = new Date().getTime();
    const startTime = parseRawInstant(pacingEntry.startDateRaw);
    const endTime = parseRawInstant(pacingEntry.endDateRaw);
    const timeStatus = deriveTimeStatus(currentTime, startTime, endTime);
    const pacingStatus = timeStatus === PacingType.ACTIVE && flightDelivered === 0
        ? PacingStatus.ACTIVE_WAITING
        : getPacingStatus(pacingOverGoal * 100, timeStatus);
    if (isPacingV2Campaign(pacingEntry)) {
        return {
            ...pacingEntry,
            timeStatus,
            pacingStatus,
            lineItems: pacingEntry.lineItems
                .map(derivePacing)
                .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
        };
    }
    return {
        ...pacingEntry,
        timeStatus,
        pacingStatus
    };
};
/**
 * Groups flat line-item rows into the campaigns the screen renders.
 *
 * Campaign-level metrics are rolled up by summing the line items (not taken from
 * Looker's lifetime-only campaign math). A campaign's `goalInterval` is `day` or
 * `lifetime` when all its line items agree, and `mixed` otherwise.
 *
 * Transport-agnostic: both row adapters land here, so the rollup and the derived
 * statuses cannot drift between the Looker query and the reporting API.
 */
const groupPacingEntries = (entries) => {
    const campaigns = {};
    // A lineitem with no campaign identity (campaignId coerced to "") can't be
    // placed in a campaign group without risking it merging with every other
    // such lineitem under the same "" key, summing unrelated metrics together
    // — drop it instead.
    entries
        .filter(({ campaignId }) => campaignId !== "")
        .forEach(({ lineItem, campaignId, campaignName, campaignStartDate, campaignEndDate, campaignStartDateRaw, campaignEndDateRaw }) => {
        const campaign = campaigns[campaignId];
        if (!campaign) {
            campaigns[campaignId] = {
                id: campaignId,
                name: campaignName,
                pacingType: lineItem.pacingType,
                goalInterval: lineItem.goalInterval,
                pacingStatus: PacingStatus.WAITING,
                timeStatus: PacingType.WAITING,
                stationName: lineItem.stationName,
                advertiserName: lineItem.advertiserName,
                advertiserId: lineItem.advertiserId,
                startDate: campaignStartDate,
                endDate: campaignEndDate,
                startDateRaw: campaignStartDateRaw,
                endDateRaw: campaignEndDateRaw,
                products: new Map(lineItem.products),
                advertiserCategoryName: lineItem.advertiserCategoryName,
                advertiserCategoryId: lineItem.advertiserCategoryId,
                periodGoal: lineItem.periodGoal,
                perDayGoal: lineItem.perDayGoal,
                periodDelivered: lineItem.periodDelivered,
                periodDeliveredImpressions: lineItem.periodDeliveredImpressions,
                periodDeliveredSpend: lineItem.periodDeliveredSpend,
                periodRemainingImpressions: lineItem.periodRemainingImpressions,
                periodRemainingBudget: lineItem.periodRemainingBudget,
                flightGoal: lineItem.flightGoal,
                flightDeliveredImpressions: lineItem.flightDeliveredImpressions,
                flightDeliveredSpend: lineItem.flightDeliveredSpend,
                flightRemainingImpressions: lineItem.flightRemainingImpressions,
                flightRemainingBudget: lineItem.flightRemainingBudget,
                lineItems: [lineItem]
            };
        }
        else {
            SUMMED_METRIC_FIELDS.forEach((field) => {
                campaign[field] += lineItem[field];
            });
            if (campaign.goalInterval !== "mixed" &&
                campaign.goalInterval !== lineItem.goalInterval) {
                campaign.goalInterval = "mixed";
            }
            lineItem.products.forEach((name, id) => campaign.products.set(id, name));
            campaign.lineItems.push(lineItem);
        }
    });
    return Object.values(campaigns)
        .map(derivePacing)
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
};
/**
 * Transforms the raw data from Looker into a normalized list of campaigns with
 * their corresponding line items.
 *
 * @param data The raw data from Looker for each line item
 * @returns A normalized list of campaigns with their line items and pacing data
 */
export const parsePacingV2Response = (data) => groupPacingEntries(data.map(fromLookerRow));
/**
 * The reporting API's rows, in the same shape. Defaults stand in for an absent
 * key: the request names every published field, so a missing one is a column
 * the row genuinely lacked rather than a field nobody asked for.
 *
 * `goal_interval` arrives already mapped to the published enum, but it still
 * goes through `normalizeGoalInterval` so one rule governs both transports.
 */
const fromApiRow = (row) => ({
    campaignId: row.campaign_id ?? "",
    campaignName: row.campaign_name ?? null,
    campaignStartDate: row.campaign_start_date ?? "",
    campaignEndDate: row.campaign_end_date ?? "",
    campaignStartDateRaw: row.campaign_start_raw ?? "",
    campaignEndDateRaw: row.campaign_end_raw ?? "",
    lineItem: {
        id: row.id ?? "",
        campaignId: row.campaign_id ?? "",
        name: row.name ?? null,
        pacingType: row.pacing_type ?? "",
        goalInterval: normalizeGoalInterval(row.goal_interval ?? ""),
        pacingStatus: PacingStatus.WAITING,
        timeStatus: PacingType.WAITING,
        periodGoal: row.period_goal ?? 0,
        perDayGoal: row.per_day_goal ?? 0,
        periodDelivered: row.period_delivered ?? 0,
        periodDeliveredImpressions: row.period_delivered_impressions ?? 0,
        periodDeliveredSpend: row.period_delivered_spend ?? 0,
        periodRemainingImpressions: row.period_remaining_impressions ?? 0,
        periodRemainingBudget: row.period_remaining_budget ?? 0,
        flightGoal: row.flight_goal ?? 0,
        flightDeliveredImpressions: row.flight_delivered_impressions ?? 0,
        flightDeliveredSpend: row.flight_delivered_spend ?? 0,
        flightRemainingImpressions: row.flight_remaining_impressions ?? 0,
        flightRemainingBudget: row.flight_remaining_budget ?? 0,
        stationName: row.station_name || "--",
        advertiserName: row.advertiser_name ?? null,
        advertiserId: row.advertiser_id ?? "",
        startDate: row.start_date ?? "",
        endDate: row.end_date ?? "",
        startDateRaw: row.start_raw ?? "",
        endDateRaw: row.end_raw ?? "",
        products: toProductsMap(row.product_id, row.product_name),
        advertiserCategoryName: row.advertiser_category_name ?? null,
        advertiserCategoryId: row.advertiser_category_id ?? null
    }
});
/** The reporting API's rows, grouped exactly as the Looker path groups its own. */
export const parsePacingApiResponse = (data) => groupPacingEntries(data.map(fromApiRow));
//# sourceMappingURL=index.js.map